// ElectricityProvider.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Swal from "sweetalert2";
import { api } from "../helper/api.interceptor";
import AddProviderModal from "./AddElectricityProvider";
import EditProviderModal from "./EditProviderDetails";

function ElectricityProvider() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [editCode, setEditCode] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchElectricityProviders = async () => {
        try {
            setLoading(true);
            const response = await api.getElectricityProviders();
            const data = response?.result || [];
            setProviders(data);
        } catch (error) {
            console.error("Error fetching electricity providers:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchElectricityProviders();
    }, []);

    const filteredProviders = providers.filter((provider) => {
        const query = searchQuery.trim().toLowerCase();
        return (
            provider.name?.toLowerCase().includes(query) ||
            provider.code?.toLowerCase().includes(query) ||
            provider.regions?.some(region => region.toLowerCase().includes(query))
        );
    });

    const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedProviders = filteredProviders.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(1);
    }, [filteredProviders, totalPages]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleDeleteClick = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await api.removeElectricityProvider(`${id}`);
                setProviders(prev => prev.filter(p => p._id !== id && p.code !== id));
                Swal.fire("Deleted!", "The provider has been deleted.", "success");
            } catch {
                Swal.fire("Error!", "Failed to delete the provider.", "error");
            }
        }
    };

    const renderBillingCycles = (cycles = []) => (
        <div className="d-flex flex-wrap gap-2">
            {cycles.map((cycle, index) => (
                <span
                    key={index}
                    className="badge rounded-pill bg-primary-light text-primary"
                    style={{ padding: "5px 10px", fontSize: "12px" }}
                >
                    {cycle}
                </span>
            ))}
        </div>
    );

    const handleItemsPerPageChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    return (
        <div className="mt-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <h6 className="fw-semibold mb-0">
                    <Icon icon="mdi:factory" className="menu-icon" style={{ width: "30px", height: "30px" }} /> | Electricity Providers
                </h6>
                <ul className="d-flex align-items-center gap-2">
                    <li className="fw-medium">
                        <Link to="/" className="d-flex align-items-center gap-1 hover-text-primary">
                            <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                            Dashboard
                        </Link>
                    </li>
                    <li> - </li>
                    <li className="fw-medium">Electricity Providers</li>
                </ul>
            </div>

            <div className="card">
                <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                            className="form-select form-select-sm w-auto no-bg-position"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value={providers.length}>All</option>
                        </select>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {/* <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        /> */}
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="btn btn-warning rounded-pill fw-medium d-flex align-items-center gap-2"
                        >
                            <Icon icon="mdi:plus" className="fs-5" />
                            Add New Provider
                        </button>
                    </div>
                </div>

                <div className="card-body table-responsive">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <table className="table bordered-table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>Logo</th>
                                        <th>Code</th>
                                        <th>Name</th>
                                        <th>Billing Cycles</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedProviders.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center">No providers found.</td>
                                        </tr>
                                    ) : (
                                        paginatedProviders.map((provider, index) => (
                                            <tr key={provider._id || provider.code || index}>
                                                <td>{startIndex + index + 1}</td>
                                                <td>
                                                    {typeof provider.image === "string" && provider.image.trim() !== "" ? (
                                                        <img
                                                            src={provider.image.replace("localhost", window.location.hostname)}
                                                            alt={provider.name || "N/A"}
                                                            style={{ width: "100px", height: "100px", objectFit: "contain" }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.remove();
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="badge bg-secondary">
                                                            {provider.name?.charAt(0).toUpperCase() || "?"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{provider.code || "N/A"}</td>
                                                <td>{provider.name || "N/A"}</td>
                                                <td>{provider.billingCycles?.length ? renderBillingCycles(provider.billingCycles) : "N/A"}</td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setEditCode(provider.code);
                                                                setShowEditModal(true);
                                                            }}
                                                            className="w-32-px h-32-px bg-warning-subtle text-warning rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                        >
                                                            <Icon icon="mdi:pencil-outline" className="fs-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(provider._id || provider.code)}
                                                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                        >
                                                            <Icon icon="mingcute:delete-2-line" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination controls */}
                            <div className="d-flex justify-content-end align-items-center gap-4 mt-3">
                                <button
                                    className="btn btn-sm"
                                    style={{
                                        backgroundColor: "#ff9800",
                                        color: "white",
                                        border: "none",
                                        padding: "10px 40px",
                                        height: "auto"
                                    }}
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>

                                <span>
                                    Page {currentPage} of {totalPages}
                                </span>

                                <button
                                    className="btn btn-sm"
                                    style={{
                                        backgroundColor: "#ff9800",
                                        color: "white",
                                        border: "none",
                                        padding: "10px 40px",
                                        height: "auto"
                                    }}
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Add Provider Modal */}
            <AddProviderModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onProviderAdded={fetchElectricityProviders}
            />
            {showEditModal && editCode && (
                <EditProviderModal
                    providerCode={editCode}
                    show={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onProviderUpdated={fetchElectricityProviders}
                />
            )}
        </div>
    );
}

export default ElectricityProvider;
